import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { ValidationMessage } from "sj-ui-toolkit/src/components";

const NOTIFICATION_OPTIONS = ["none", "daily", "weekly", "monthly", "quarterly"] as const;
const NAME_TEMPLATE = "Personalized Assignment for {User Name} — {Month Year}";

const reminderTextMap: Record<string, string> = {
  None: "No reminder emails will be sent.",
  Daily: "Learners will receive reminder emails every day until the assessment is complete.",
  Weekly: "Learners will receive reminder emails every week until the assessment is complete.",
  Monthly: "Learners will receive reminder emails every month until the assessment is complete.",
  Quarterly:
    "Learners will receive reminder emails every quarter until the assessment is complete.",
};

const normalizeToMidnight = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

const setToEndOfDay = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate;
};

const findDaysBetween = (startDate: Date, endDate: Date) => {
  const start = normalizeToMidnight(startDate);
  const end = normalizeToMidnight(endDate);
  if (end < start) return 0;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export interface TrainingAssignmentSettingsValues {
  name: string;
  description: string;
  startDate: string | null;
  endDate: string | null;
  reminders: string;
}

export interface TrainingAssignmentSettingsValidationErrors {
  description?: { hasError?: boolean; message?: string };
  startDate?: { hasError?: boolean; message?: string };
  timeframe?: { hasError?: boolean; message?: string };
}

interface TrainingAssignmentSettingsProps {
  settings: TrainingAssignmentSettingsValues;
  onSettingsChange: (settings: TrainingAssignmentSettingsValues) => void;
  showValidationMessages?: boolean;
  validationErrors?: TrainingAssignmentSettingsValidationErrors;
  hideDaily?: boolean;
  hideNameDescription?: boolean;
}

export default function TrainingAssignmentSettings({
  settings,
  onSettingsChange,
  showValidationMessages = false,
  validationErrors = {},
  hideDaily = false,
  hideNameDescription = false,
}: TrainingAssignmentSettingsProps) {
  const reminderOptions = hideDaily
    ? NOTIFICATION_OPTIONS.filter(option => option !== "daily")
    : NOTIFICATION_OPTIONS;

  const startDate = settings.startDate ? new Date(settings.startDate) : null;
  const endDate = settings.endDate ? new Date(settings.endDate) : null;
  const daysBetween = startDate && endDate ? findDaysBetween(startDate, endDate) : 0;

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onSettingsChange({ ...settings, description: event.target.value });
  };

  const handleStartDateChange: NonNullable<
    React.ComponentProps<typeof Calendar>["onChange"]
  > = event => {
    const nextDate = event.value instanceof Date ? event.value : null;
    onSettingsChange({
      ...settings,
      startDate: nextDate ? normalizeToMidnight(nextDate).toISOString() : null,
    });
  };

  const handleEndDateChange: NonNullable<
    React.ComponentProps<typeof Calendar>["onChange"]
  > = event => {
    const nextDate = event.value instanceof Date ? event.value : null;
    onSettingsChange({
      ...settings,
      endDate: nextDate ? setToEndOfDay(nextDate).toISOString() : null,
    });
  };

  return (
    <div>
      {!hideNameDescription && (
        <>
          <div className="mb-8">
            <InputText
              id="training-name"
              value={settings.name || NAME_TEMPLATE}
              className="w-full"
              disabled
            />
          </div>

          <div className="mb-4">
            <InputTextarea
              id="training-description"
              value={settings.description}
              onChange={handleDescriptionChange}
              rows={5}
              className="w-full"
              invalid={!settings.description?.trim()}
              placeholder="Enter a description for this assignment"
            />
            {showValidationMessages && validationErrors.description?.hasError && (
              <div role="alert">
                <ValidationMessage
                  severity="error"
                  text={validationErrors.description.message ?? ""}
                />
              </div>
            )}
          </div>
        </>
      )}

      <div className="grid gap-6 lg:grid-cols-2 lg:gap-12">
        <div>
          <div className="mb-4">
            <label htmlFor="training-start-date" className="mb-2 block">
              Start Date
            </label>
            <Calendar
              id="training-start-date"
              value={startDate}
              minDate={new Date()}
              placeholder="Select start date"
              onChange={handleStartDateChange}
              showIcon
              className={!startDate ? "p-invalid" : ""}
            />
            {showValidationMessages && validationErrors.startDate?.hasError && (
              <div role="alert">
                <ValidationMessage
                  severity="error"
                  text={validationErrors.startDate.message ?? ""}
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="training-end-date" className="mb-2 block">
              End Date
            </label>
            <Calendar
              id="training-end-date"
              value={endDate}
              minDate={startDate ?? new Date()}
              placeholder="Select end date"
              onChange={handleEndDateChange}
              showIcon
              className={!endDate ? "p-invalid" : ""}
            />
            <div className="mt-2 body-sm text-secondary">{`${daysBetween} days`}</div>
            {showValidationMessages && validationErrors.timeframe?.hasError && (
              <div role="alert">
                <ValidationMessage
                  severity="error"
                  text={validationErrors.timeframe.message ?? ""}
                />
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mb-4">
            <p>Notify Learners:</p>
            <div className="mt-2">
              <p className="body-sm">
                Learners will get notified as soon as the assignment is created.
              </p>
            </div>
          </div>

          <div>
            <p>Send reminders:</p>
            {reminderOptions.map(option => {
              const tooltipText = reminderTextMap[capitalize(option)];

              return (
                <div key={option} className="mt-2 flex items-center gap-2">
                  <RadioButton
                    inputId={`training-reminder-${option}`}
                    name="trainingReminders"
                    value={option}
                    onChange={() => onSettingsChange({ ...settings, reminders: option })}
                    checked={settings.reminders === option}
                  />
                  <label htmlFor={`training-reminder-${option}`}>{capitalize(option)}</label>
                  {tooltipText && (
                    <>
                      <FontAwesomeIcon
                        id={`training-notification-tooltip-${option}`}
                        icon={faQuestionCircle}
                        size="sm"
                        style={{ marginLeft: "0.1rem", cursor: "pointer" }}
                      />
                      <Tooltip target={`#training-notification-tooltip-${option}`}>
                        {tooltipText}
                      </Tooltip>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
